/**
 * Teaches the Monaco TypeScript language service about the ODIN Cortex Serverless Function
 * authoring surface so the function editor offers fully-typed IntelliSense.
 *
 * ## Why this exists
 *
 * Cortex functions are authored in TypeScript and use bare `exports.onMessageCreated = …`
 * handlers (no imports). To make `event` / `ctx` arguments and global event types
 * (`MessageCreatedEvent`, `Context`, …) resolve in the editor, we inject an **ambient**
 * declaration bundle — `CORTEX_FUNCTION_EDITOR_DTS`, generated **in this repo** from our own
 * `openapi-spec.json` (see tools/generate-function-editor-types.mjs). It is intentionally not
 * part of the public `@4players/odin-cortex` SDK. Because the bundle declares
 * `exports`/`module.exports` as `CortexFunctionExports`, assigning a handler contextually
 * types its arguments.
 *
 * Invoked once from `provideMonacoEditor({ onMonacoLoad })` in `app.config.ts`. The (large)
 * generated declaration string is imported dynamically so it loads lazily alongside the
 * Monaco runtime rather than bloating the initial application bundle. Because it is a local
 * source module (not a node_modules dependency), the dev server recompiles it on change —
 * there is no pre-bundled-dependency cache to go stale.
 */

/** Stable virtual path for the injected ambient declaration file. */
const CORTEX_FUNCTION_DTS_PATH = 'inmemory://model/cortex-functions.d.ts';

/**
 * Configure Monaco's TypeScript defaults for Cortex function authoring and inject the
 * Cortex ambient typings. Safe to call before the SDK chunk has loaded — the extra lib is
 * added once the dynamic import resolves.
 */
export function configureCortexFunctionEditor(): void {
  // `monaco` is attached to the global scope by the Monaco loader (ngx-monaco-editor-v2).
  const monaco = (globalThis as unknown as { monaco?: any }).monaco;
  const tsDefaults = monaco?.languages?.typescript;
  if (!tsDefaults?.typescriptDefaults) {
    return;
  }

  // Function code is CommonJS-style (`exports.x = …`) and never bundled, so allow JS,
  // resolve as CommonJS, and skip emit.
  //
  // NOTE: `lib` has no effect in Monaco's TS worker - it always loads `lib.es<target>.full.d.ts`,
  // which includes the DOM. It is kept here to document the intent; the globals the runtime
  // really provides are declared in the ambient bundle below.
  tsDefaults.typescriptDefaults.setCompilerOptions({
    target: tsDefaults.ScriptTarget.ES2020,
    module: tsDefaults.ModuleKind.CommonJS,
    moduleResolution: tsDefaults.ModuleResolutionKind.NodeJs,
    allowJs: true,
    allowNonTsExtensions: true,
    noEmit: true,
    lib: ['es2020'],
    // Dependency imports (`import Mailjet from 'node-mailjet'`) are transpiled to
    // `require(...)` by esbuild server-side, so allow default-import interop here to match.
    esModuleInterop: true,
    allowSyntheticDefaultImports: true,
  });

  // The runtime wraps function code in a CommonJS module scope, so a top-level `const crypto =
  // require('crypto')` legally shadows the global of the same name from the DOM lib. Monaco checks
  // the code as a top-level script and reports TS2451 ("Cannot redeclare block-scoped variable"),
  // which would block publishing code that runs fine. TS2403 is the `var` variant of the same
  // situation. Hiding these keeps the gate on errors that actually break a deployment.
  tsDefaults.typescriptDefaults.setDiagnosticsOptions({
    diagnosticCodesToIgnore: [
      2451, // Cannot redeclare block-scoped variable
      2403, // Subsequent variable declarations must have the same type
    ],
  });

  // Pull the ambient declaration string lazily so it stays out of the main bundle, then
  // register it as an extra library for the TS language service. The bundle is self-contained:
  // it embeds the `@4players/odin-cortex` SDK as a global `CortexSDK` namespace, so `ctx.cortex`
  // resolves with no module resolution (see tools/generate-function-editor-types.mjs).
  import('./cortex-function-editor-types.generated')
    .then(({ CORTEX_FUNCTION_EDITOR_DTS }) => {
      if (!CORTEX_FUNCTION_EDITOR_DTS) {
        console.warn(
          '[Cortex] CORTEX_FUNCTION_EDITOR_DTS is empty. Function editor IntelliSense ' +
            'is disabled — run `npm run generate:function-types` to regenerate it.',
        );
        return;
      }
      tsDefaults.typescriptDefaults.addExtraLib(
        CORTEX_FUNCTION_EDITOR_DTS,
        CORTEX_FUNCTION_DTS_PATH,
      );
    })
    .catch((err) => {
      // Non-fatal: the editor still works, just without Cortex-specific IntelliSense.
      console.warn('[Cortex] Failed to load function editor typings:', err);
    });
}
